import {
  ConnectionQuality,
  ConnectionState,
  DisconnectReason,
  Room,
  RoomEvent,
  Track,
  VideoQuality,
  type LocalTrackPublication,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import type { PeerStats, Quality } from './Peer';

/**
 * The tracks this person should be sending right now. MeetingSession owns them (device choice,
 * mute, camera off); this class only mirrors them onto LiveKit.
 */
export interface WantedMedia {
  mic: MediaStreamTrack | null;
  micMuted: boolean;
  camera: MediaStreamTrack | null;
  screen: MediaStreamTrack | null;
  screenAudio: MediaStreamTrack | null;
  /** Zoom's "Pause share": the others keep the last frame. */
  screenPaused: boolean;
}

export type StreamKind = 'camera' | 'screen';

interface Events {
  /** A remote participant's camera (voice + camera) or screen (screen + its sound) stream changed. */
  stream(identity: string, kind: StreamKind, stream: MediaStream): void;
  /** A remote participant is no longer in the media room (left, moved, or we left it). */
  gone(identity: string): void;
  stats(identity: string, stats: PeerStats): void;
  /** The media server stopped us or could not be reached; a fresh credential may help. */
  needCredentials(): void;
  failed(message: string): void;
}

/**
 * Stream ids announced in the participant state (cameraStreamId / screenStreamId). With LiveKit
 * they are derived from the participant id, so every receiver can find the right stream.
 */
export function liveKitStreamId(identity: string, kind: StreamKind): string {
  return `lk-${kind}-${identity}`;
}

const SOURCES = [Track.Source.Microphone, Track.Source.Camera, Track.Source.ScreenShare, Track.Source.ScreenShareAudio] as const;
type Source = (typeof SOURCES)[number];
/** Numeric TrackSource values LiveKit uses in permissions. */
const PROTO_SOURCE: Record<Source, number> = { [Track.Source.Camera]: 1, [Track.Source.Microphone]: 2, [Track.Source.ScreenShare]: 3, [Track.Source.ScreenShareAudio]: 4 };

const QUALITY: Record<ConnectionQuality, Quality> = {
  [ConnectionQuality.Excellent]: 'good',
  [ConnectionQuality.Good]: 'good',
  [ConnectionQuality.Poor]: 'fair',
  [ConnectionQuality.Lost]: 'poor',
  [ConnectionQuality.Unknown]: 'unknown',
};

/**
 * Audio and video through a LiveKit media server (SFU): each person sends their camera, voice and
 * screen once, and LiveKit forwards them to everyone in the same media room. Which room, and what
 * may be published, is decided by the Nexo server and arrives as a credential (`connect`).
 */
export class LiveKitMedia {
  private room: Room | null = null;
  private target: { url: string; room: string; token: string } | null = null;
  private wanted: WantedMedia = { mic: null, micMuted: true, camera: null, screen: null, screenAudio: null, screenPaused: false };
  private published = new Map<Source, { pub: LocalTrackPublication; track: MediaStreamTrack }>();
  private streams = new Map<string, MediaStream>();
  private syncing: Promise<void> = Promise.resolve();
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay = 1000;
  private closed = false;

  constructor(private readonly events: Events) {}

  get roomName(): string | null {
    return this.room?.state === ConnectionState.Connected ? this.room.name : null;
  }

  /** Joins the media room this credential is for, leaving any other one first. */
  connect(url: string | null, roomName: string, token: string): void {
    if (this.closed) return;
    const resolved = url ?? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/livekit`;
    const same = this.target?.room === roomName && this.target.url === resolved && this.room && this.room.state !== ConnectionState.Disconnected;
    this.target = { url: resolved, room: roomName, token };
    if (same) return;
    this.clearRetry();
    void this.open();
  }

  /** Leaves the media room (waiting room, meeting over). */
  disconnect(): void {
    this.target = null;
    this.clearRetry();
    void this.leaveRoom();
  }

  dispose(): void {
    this.closed = true;
    this.disconnect();
  }

  setLocal(wanted: WantedMedia): void {
    this.wanted = wanted;
    this.sync();
  }

  // ───────────────────────────── connection ─────────────────────────────

  private async open(): Promise<void> {
    const target = this.target;
    if (!target) return;
    await this.leaveRoom();
    if (this.target !== target) return;
    const room = new Room({
      // Nexo renders the tracks itself, so LiveKit cannot see the video elements: no adaptive
      // stream. Dynacast still stops layers nobody receives.
      adaptiveStream: false,
      dynacast: true,
      // The tracks belong to MeetingSession (they survive room changes and reconnections).
      stopLocalTrackOnUnpublish: false,
      publishDefaults: { simulcast: true, dtx: true, red: true },
    });
    this.room = room;
    this.wire(room);
    try {
      await room.connect(target.url, target.token, { autoSubscribe: true });
    } catch (err) {
      if (this.room !== room) return;
      console.warn('[livekit] no se pudo conectar', err);
      this.room = null;
      void room.disconnect(false);
      this.events.failed('No se puede conectar con el servidor de audio y vídeo. Reintentando…');
      this.scheduleRetry();
      return;
    }
    if (this.room !== room) return void room.disconnect(false);
    this.retryDelay = 1000;
    for (const p of room.remoteParticipants.values()) this.onQuality(p);
    this.applyQuality();
    // A pass left over from a previous room may never settle (its publish died with that
    // connection): start a fresh queue so this room gets published.
    this.syncing = Promise.resolve();
    this.sync();
  }

  private wire(room: Room): void {
    room
      .on(RoomEvent.TrackSubscribed, (track, pub, participant) => this.onTrack(track, pub, participant, true))
      .on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => this.onTrack(track, pub, participant, false))
      .on(RoomEvent.ParticipantConnected, (p) => {
        this.onQuality(p);
        this.applyQuality();
      })
      .on(RoomEvent.ParticipantDisconnected, (p) => {
        this.dropStreams(p.identity);
        this.applyQuality();
      })
      .on(RoomEvent.ConnectionQualityChanged, (_q, p) => {
        if (p !== room.localParticipant) this.onQuality(p as RemoteParticipant);
      })
      .on(RoomEvent.Reconnecting, () => {
        for (const p of room.remoteParticipants.values()) this.events.stats(p.identity, { state: 'connecting', quality: 'unknown', rttMs: null, lossPct: null });
      })
      .on(RoomEvent.Reconnected, () => {
        for (const p of room.remoteParticipants.values()) this.onQuality(p);
      })
      // The Nexo server changed what we may publish (screen share approved or stopped, unmute forbidden).
      .on(RoomEvent.ParticipantPermissionsChanged, (_prev, p) => {
        if (p === room.localParticipant) this.sync();
      })
      .on(RoomEvent.LocalTrackUnpublished, (pub) => {
        for (const [source, entry] of this.published) if (entry.pub === pub) this.published.delete(source);
      })
      .on(RoomEvent.Disconnected, (reason) => {
        if (this.room !== room) return;
        this.room = null;
        this.published.clear();
        for (const identity of new Set([...this.streams.keys()].map((k) => k.slice(k.indexOf(':') + 1)))) this.dropStreams(identity);
        // Removed by the server: usually a credential for another room is on its way (or we are
        // no longer allowed in). If we still hold one for this very room, ask again: the server
        // only answers with a room we may be in. Anything else (network, server restart): ask again.
        if (!this.target || reason === DisconnectReason.CLIENT_INITIATED) return;
        if (reason !== DisconnectReason.PARTICIPANT_REMOVED || this.target.room === room.name) this.scheduleRetry();
      });
  }

  private scheduleRetry(): void {
    if (this.retryTimer || this.closed) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.target || this.room) return;
      // The credential may have expired meanwhile; the server sends a new one, which reconnects.
      this.events.needCredentials();
    }, this.retryDelay);
    this.retryDelay = Math.min(this.retryDelay * 2, 15_000);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private async leaveRoom(): Promise<void> {
    const room = this.room;
    this.room = null;
    this.published.clear();
    if (!room) return;
    for (const p of room.remoteParticipants.values()) this.dropStreams(p.identity);
    await room.disconnect(false).catch(() => undefined);
  }

  // ───────────────────────────── remote media ─────────────────────────────

  private onTrack(track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant, added: boolean): void {
    const kind: StreamKind = pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio ? 'screen' : 'camera';
    const key = `${kind}:${participant.identity}`;
    let stream = this.streams.get(key);
    if (!stream) {
      if (!added) return;
      // One stable stream per person and kind, like the mesh: tracks come and go inside it.
      this.streams.set(key, (stream = new MediaStream()));
    }
    const media = track.mediaStreamTrack;
    if (added) {
      for (const t of track.kind === Track.Kind.Video ? stream.getVideoTracks() : stream.getAudioTracks()) if (t !== media) stream.removeTrack(t);
      if (!stream.getTracks().includes(media)) stream.addTrack(media);
      if (kind === 'camera' && track.kind === Track.Kind.Video) this.applyQuality();
    } else {
      stream.removeTrack(media);
    }
    this.events.stream(participant.identity, kind, stream);
  }

  private dropStreams(identity: string): void {
    for (const kind of ['camera', 'screen'] as const) this.streams.delete(`${kind}:${identity}`);
    this.events.gone(identity);
  }

  private onQuality(p: RemoteParticipant): void {
    this.events.stats(p.identity, { state: 'connected', quality: QUALITY[p.connectionQuality] ?? 'unknown', rttMs: null, lossPct: null });
  }

  /**
   * Large meetings: receive smaller camera layers (simulcast) so a gallery of many people fits
   * in everyone's download. Shared screens always come at full quality.
   */
  private applyQuality(): void {
    const room = this.room;
    if (!room) return;
    // Tiles get smaller the more of them are on screen; off-screen cameras are not received at all.
    const shown = this.visible ? this.visible.size : room.remoteParticipants.size;
    const quality = shown <= 2 ? VideoQuality.HIGH : shown <= 6 ? VideoQuality.MEDIUM : VideoQuality.LOW;
    for (const p of room.remoteParticipants.values()) {
      for (const pub of p.trackPublications.values()) {
        if (pub.source === Track.Source.Camera) {
          const wanted = !this.visible || this.visible.has(p.identity);
          if (pub.isSubscribed && pub.isEnabled !== wanted) pub.setEnabled(wanted);
          if (wanted && pub.isSubscribed) pub.setVideoQuality(quality);
        } else if (pub.source === Track.Source.ScreenShare) pub.setVideoQuality(VideoQuality.HIGH);
      }
    }
  }

  /**
   * Whose camera is on screen (the current gallery page, the filmstrip). The others' cameras are
   * paused on LiveKit's side: with 50 people nobody downloads or decodes 49 videos. Voices always
   * play. null = everyone (small meetings).
   */
  setVisible(ids: Iterable<string> | null): void {
    const next = ids ? new Set(ids) : null;
    const same = next && this.visible ? next.size === this.visible.size && [...next].every((id) => this.visible!.has(id)) : next === this.visible;
    if (same) return;
    this.visible = next;
    this.applyQuality();
  }

  private visible: Set<string> | null = null;

  // ───────────────────────────── local media ─────────────────────────────

  /** Serialized: each pass compares what is published with what is wanted and fixes the difference. */
  private sync(): void {
    this.syncing = this.syncing
      .then(() => this.reconcile())
      .catch((err) => {
        // A publish can time out on a busy network or machine: try again, or this person stays
        // without camera and microphone for everyone until something else changes.
        console.warn('[livekit] publicación', err);
        if (this.syncRetry || this.closed) return;
        this.syncRetry = setTimeout(() => {
          this.syncRetry = null;
          if (this.room && !this.closed) this.sync();
        }, 2000);
      });
  }

  private syncRetry: ReturnType<typeof setTimeout> | null = null;

  private allowed(source: Source): boolean {
    const perms = this.room?.localParticipant.permissions;
    if (!perms) return true;
    if (!perms.canPublish) return false;
    return !perms.canPublishSources.length || perms.canPublishSources.includes(PROTO_SOURCE[source] as never);
  }

  private async reconcile(): Promise<void> {
    const room = this.room;
    if (!room || room.state !== ConnectionState.Connected) return;
    const w = this.wanted;
    const want: Record<Source, MediaStreamTrack | null> = {
      [Track.Source.Microphone]: w.mic?.readyState === 'live' ? w.mic : null,
      [Track.Source.Camera]: w.camera?.readyState === 'live' ? w.camera : null,
      [Track.Source.ScreenShare]: w.screen?.readyState === 'live' ? w.screen : null,
      [Track.Source.ScreenShareAudio]: w.screenAudio?.readyState === 'live' ? w.screenAudio : null,
    };
    for (const source of SOURCES) {
      if (this.room !== room) return;
      const track = this.allowed(source) ? want[source] : null;
      const current = this.published.get(source);
      if (!track) {
        if (current) {
          this.published.delete(source);
          await room.localParticipant.unpublishTrack(current.pub.track!, false).catch(() => undefined);
        }
        continue;
      }
      if (current && current.track !== track) {
        // New device or new screen source: swap without renegotiating.
        await current.pub.track?.replaceTrack(track, { userProvidedTrack: true });
        current.track = track;
      } else if (!current) {
        const pub = await room.localParticipant.publishTrack(track, {
          source,
          name: source,
          ...(source === Track.Source.ScreenShare ? { simulcast: false } : {}),
        });
        if (this.room !== room) return;
        this.published.set(source, { pub, track });
      }
      const entry = this.published.get(source)!;
      const localTrack = entry.pub.track;
      if (!localTrack) continue;
      if (source === Track.Source.Microphone) {
        if (w.micMuted && !localTrack.isMuted) await localTrack.mute();
        else if (!w.micMuted && localTrack.isMuted) await localTrack.unmute();
      }
      if (source === Track.Source.ScreenShare || source === Track.Source.ScreenShareAudio) {
        // Pausing stops sending without unpublishing, so the others keep the last frame.
        if (w.screenPaused && !localTrack.isUpstreamPaused) await localTrack.pauseUpstream();
        else if (!w.screenPaused && localTrack.isUpstreamPaused) await localTrack.resumeUpstream();
      }
    }
  }
}
